import { useState } from "react";
import { CheckCircle2, Loader2, Mail, XCircle } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useEmailConfig,
  useSendTestEmail,
  useTestHistory,
} from "@/hooks/useEmailTesting";
import { ApiError } from "@/services/api";
import type {
  EmailConfig,
  EmailProviderType,
  EmailTestLogEntry,
  EmailTestTemplate,
  ProviderOverride,
  TestEmailRequest,
  TestSendResult,
} from "@/services/emailApi";

const TEMPLATE_OPTIONS: { value: EmailTestTemplate; label: string }[] = [
  { value: "password_reset", label: "Password Reset" },
  { value: "business_invite", label: "Business Invite" },
  { value: "alert_notification", label: "Alert Notification" },
];

const PROVIDER_OPTIONS: { value: EmailProviderType; label: string }[] = [
  { value: "mailtrap", label: "MailTrap" },
  { value: "gmail", label: "Gmail" },
  { value: "sendgrid", label: "SendGrid" },
  { value: "brevo", label: "Brevo" },
];

const API_KEY_PROVIDERS = new Set<EmailProviderType>(["brevo", "sendgrid"]);

interface TestEmailFormState {
  recipientEmail: string;
  template: EmailTestTemplate;
  useCustomProvider: boolean;
  providerType: EmailProviderType;
  apiKey: string;
  host: string;
  port: string;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
}

const defaultForm: TestEmailFormState = {
  recipientEmail: "",
  template: "password_reset",
  useCustomProvider: false,
  providerType: "mailtrap",
  apiKey: "",
  host: "",
  port: "",
  username: "",
  password: "",
  fromEmail: "",
  fromName: "",
};

function handleApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    toast.error(error.message);
    return;
  }
  toast.error(error instanceof Error ? error.message : fallback);
}

function buildOverride(form: TestEmailFormState): ProviderOverride {
  const isApiKeyProvider = API_KEY_PROVIDERS.has(form.providerType);
  const trimmedPort = form.port.trim();
  return {
    provider_type: form.providerType,
    ...(isApiKeyProvider && form.apiKey.trim()
      ? { api_key: form.apiKey.trim() }
      : {}),
    ...(!isApiKeyProvider && form.host.trim()
      ? { host: form.host.trim() }
      : {}),
    ...(!isApiKeyProvider && trimmedPort ? { port: Number(trimmedPort) } : {}),
    ...(!isApiKeyProvider && form.username.trim()
      ? { username: form.username.trim() }
      : {}),
    ...(!isApiKeyProvider && form.password.trim()
      ? { password: form.password.trim() }
      : {}),
    ...(form.fromEmail.trim() ? { from_email: form.fromEmail.trim() } : {}),
    ...(form.fromName.trim() ? { from_name: form.fromName.trim() } : {}),
  };
}

function isFormValid(form: TestEmailFormState): boolean {
  if (!form.recipientEmail.trim()) return false;
  if (!form.useCustomProvider) return true;
  if (API_KEY_PROVIDERS.has(form.providerType)) {
    return form.apiKey.trim().length > 0;
  }
  return (
    form.host.trim().length > 0 &&
    form.username.trim().length > 0 &&
    form.password.trim().length > 0
  );
}

export default function EmailTesting() {
  const { data: config, isLoading: configLoading } = useEmailConfig();
  const { data: history, isLoading: historyLoading } = useTestHistory();
  const sendTestEmail = useSendTestEmail();

  const [form, setForm] = useState<TestEmailFormState>(defaultForm);
  const [result, setResult] = useState<TestSendResult | null>(null);

  const isApiKeyProvider = API_KEY_PROVIDERS.has(form.providerType);

  async function handleSend() {
    const payload: TestEmailRequest = {
      recipient_email: form.recipientEmail.trim(),
      template: form.template,
      ...(form.useCustomProvider ? { override: buildOverride(form) } : {}),
    };
    try {
      const sendResult = await sendTestEmail.mutateAsync(payload);
      setResult(sendResult);
      if (sendResult.success) {
        toast.success(`Test email sent via ${sendResult.provider}.`);
      } else {
        toast.error(sendResult.error ?? "Test send failed.");
      }
    } catch (error) {
      setResult(null);
      handleApiError(error, "Failed to send test email.");
    }
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" theme="dark" />

      <div className="flex items-center gap-3">
        <Mail className="size-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold">Email Testing</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Verify email sending and test arbitrary provider credentials without
            changing environment configuration.
          </p>
        </div>
      </div>

      <EnvironmentConfigCard config={config} isLoading={configLoading} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Send Test Email</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Renders a real template with sample data and sends it once.
            Credentials below are never stored or logged.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="recipient-email">Recipient Email</Label>
              <Input
                id="recipient-email"
                type="email"
                value={form.recipientEmail}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    recipientEmail: e.target.value,
                  }))
                }
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template">Template</Label>
              <Select
                value={form.template}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    template: value as EmailTestTemplate,
                  }))
                }
              >
                <SelectTrigger id="template">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="use-custom-provider">Use custom provider</Label>
              <p className="text-sm text-muted-foreground">
                Override the environment default to test one-off credentials
                (e.g. a production key from dev).
              </p>
            </div>
            <Switch
              id="use-custom-provider"
              checked={form.useCustomProvider}
              onCheckedChange={(checked) =>
                setForm((prev) => ({ ...prev, useCustomProvider: checked }))
              }
            />
          </div>

          {form.useCustomProvider && (
            <div className="space-y-4 rounded-md border p-4">
              <div className="space-y-2">
                <Label htmlFor="provider-type">Provider</Label>
                <Select
                  value={form.providerType}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      providerType: value as EmailProviderType,
                    }))
                  }
                >
                  <SelectTrigger id="provider-type" className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDER_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isApiKeyProvider ? (
                <div className="space-y-2">
                  <Label htmlFor="api-key">API Key</Label>
                  <Input
                    id="api-key"
                    type="password"
                    autoComplete="off"
                    value={form.apiKey}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, apiKey: e.target.value }))
                    }
                    placeholder="Used for this one-off send only"
                  />
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="smtp-host">Host</Label>
                    <Input
                      id="smtp-host"
                      value={form.host}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, host: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-port">Port</Label>
                    <Input
                      id="smtp-port"
                      type="number"
                      value={form.port}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, port: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-username">Username</Label>
                    <Input
                      id="smtp-username"
                      value={form.username}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          username: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-password">Password</Label>
                    <Input
                      id="smtp-password"
                      type="password"
                      autoComplete="off"
                      value={form.password}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="from-email">From Email (optional)</Label>
                  <Input
                    id="from-email"
                    type="email"
                    value={form.fromEmail}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        fromEmail: e.target.value,
                      }))
                    }
                    placeholder="Required by Brevo/SendGrid for unverified senders"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="from-name">From Name (optional)</Label>
                  <Input
                    id="from-name"
                    value={form.fromName}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        fromName: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button
              onClick={handleSend}
              disabled={!isFormValid(form) || sendTestEmail.isPending}
            >
              {sendTestEmail.isPending && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Send Test Email
            </Button>
          </div>

          {result && (
            <div
              className={
                result.success
                  ? "rounded-md border border-emerald-500/50 bg-emerald-500/5 p-3 text-sm"
                  : "rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm"
              }
            >
              <div className="flex items-center gap-2 font-medium">
                {result.success ? (
                  <CheckCircle2 className="size-4 text-emerald-600" />
                ) : (
                  <XCircle className="size-4 text-destructive" />
                )}
                {result.success ? "Sent successfully" : "Send failed"}
              </div>
              <div className="mt-1 text-muted-foreground">
                Provider: {result.provider}
              </div>
              {result.error && (
                <div className="mt-1 font-mono text-xs text-destructive">
                  {result.error}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <TestHistoryTable history={history ?? []} isLoading={historyLoading} />
    </div>
  );
}

function EnvironmentConfigCard({
  config,
  isLoading,
}: {
  config: EmailConfig | undefined;
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Environment Defaults</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Used when no custom provider override is supplied below.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading || !config ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="text-xs text-muted-foreground">
                Active Provider
              </div>
              <div className="font-medium">{config.active_email_provider}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Environment</div>
              <div className="font-medium">{config.app_env}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">From</div>
              <div className="font-medium">
                {config.from_name} &lt;{config.from_email}&gt;
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Configured</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {Object.entries(config.configured).map(
                  ([provider, isConfigured]) => (
                    <Badge
                      key={provider}
                      variant={isConfigured ? "default" : "outline"}
                    >
                      {provider}
                    </Badge>
                  ),
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TestHistoryTable({
  history,
  isLoading,
}: {
  history: EmailTestLogEntry[];
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent Test Sends</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Recipient</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Error</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Sent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Loader2 className="size-4 animate-spin" />
                </TableCell>
              </TableRow>
            ) : history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground">
                  No test sends yet.
                </TableCell>
              </TableRow>
            ) : (
              history.map((entry, index) => (
                <TableRow key={`${entry.created_at}-${index}`}>
                  <TableCell>{entry.recipient}</TableCell>
                  <TableCell className="max-w-xs truncate">
                    {entry.subject}
                  </TableCell>
                  <TableCell>{entry.provider_name ?? "—"}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        entry.status === "sent"
                          ? "default"
                          : entry.status === "failed"
                            ? "destructive"
                            : "secondary"
                      }
                    >
                      {entry.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-xs text-muted-foreground">
                    {entry.error_message ?? "—"}
                  </TableCell>
                  <TableCell>
                    {new Date(entry.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {entry.sent_at
                      ? new Date(entry.sent_at).toLocaleString()
                      : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
